import { GroupCondition } from '@/api/enums';
import {
  contains,
  equal,
  greater,
  group,
  handleAxiosError,
  lower,
  notEqual,
} from '@/api/helpers';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import {
  ContentSeries,
  Course,
  CourseScheduleSource,
  CourseStatus,
  MaterialType,
} from '@/client/courses';
import {
  CoursePlanner,
  CourseSchedulePeriodUnit,
  PlannerCRUDCourse,
  PlannerTemplateType,
  PlannerType,
} from '@/client/planner/types';
import { Actions, Subjects } from '@/client/users';
import { DEFAULT_ENROLLMENT_HOUR, LoadingStatuses } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { ExcludeFormValue, TargetFormValue } from '@/common/types';
import { InlineBlockEmails } from '@/components/block-emails/InlineBlockEmails';
import { useCoursesPartialRequest } from '@/hooks/query';
import {
  useCreatePlanner,
  useDeletePlanner,
  useUpdatePlanner,
} from '@/hooks/query/planner.hooks';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { EditableText } from '@/ui/editable-text';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { TargetEntitiesSelector } from '@/ui/target-selector';
import { formatDate } from '@/utils/date/date.helper';
import {
  isDemoRestrictedMode,
  sanitizeText,
  showBlockEmailsMessage,
  TrashIcon,
} from '@/utils/helpers';
import { getSortedCoursesInPlanner } from '@/utils/planner';
import {
  getTargetsAndExcludes,
  processIncludeExcludeTargetEntities,
} from '@/utils/targets';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import moment from 'moment';
import { Dialog, DialogProps } from 'primereact/dialog';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AutomaticSchedulingToggles } from '../AutomaticSchedulingToggles';
import { PlannerCRUDTimeline } from '../PlannerCRUDTimeline';
import {
  PendingSeriesToggle,
  SeriesToggleConfirmDialog,
} from './SeriesToggleConfirmDialog';

const StyledDialog = styled(Dialog)`
  width: 100%;
  max-width: 1400px;
  max-height: 850px;
  height: 100%;

  .p-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 0;
  }

  .p-dialog-header .p-dialog-header-icons {
    margin: auto;
    margin-right: 0;
  }

  .p-dialog-footer {
    background-color: var(--purple-dark);
  }
`;

const StyledGrayFlexContainer = styled(FlexContainer)`
  background-color: var(--beige-main);
  padding: var(--big-padding);
  padding-bottom: 0;
  margin-top: var(--default-padding);
  height: 100%;
`;

const StyledBottomDiv = styled.div`
  width: 100%;
  padding-top: 0;
  border-top: 1px solid var(--gray-dark);

  > div {
    padding: var(--default-padding);
  }
`;

const StyledModalFooter = styled(FlexContainer)`
  background-color: var(--purple-dark);

  svg path {
    fill: var(--purple-light);
  }
`;

const StyledDeleteAction = styled.div`
  display: flex;
  cursor: pointer;

  &:hover {
    svg path {
      fill: var(--white-main);
    }
  }

  &.disabled {
    pointer-events: none;
  }
`;

const StyledFooterText = styled.span`
  color: var(--white-main);
  font-size: var(--xsmall-font-size);

  &:not(.delete-info) {
    position: relative;
    margin-left: var(--small-padding);

    &:before {
      content: '';
      position: absolute;
      border-left: 1px solid var(--purple-light);
      left: -12px;
      height: 14px;
      top: 50%;
      transform: translate(0, -50%);
    }
  }
`;

const StyledHeaderContainer = styled(FlexContainer)`
  gap: var(--default-padding);
  align-items: center;
  flex-wrap: wrap;

  h1 {
    max-width: 100%;
  }
`;

const StyledDialogContainer = styled.div`
  &:not(:first-child) {
    margin-top: var(--medium-padding);
  }

  .badge {
    background: var(--gray-medium);
    font-size: var(--xxsmall-font-size);
    padding: 2px var(--small-padding);
    color: var(--black-main);
    border-radius: 100px;
    display: inline-block;
    line-height: 15px;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
  }
`;

const StyledMakePlanActiveRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--medium-padding);
  padding: var(--medium-padding) var(--big-padding);
  border-top: 1px solid var(--gray-pale);
  width: 100%;
  box-sizing: border-box;

  .on-off-text {
    top: 0;
  }

  .switch-container {
    display: flex;
  }
`;

const StyledMakePlanActiveLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  flex-shrink: 0;
`;

const StyledMakePlanActiveLabel = styled.p`
  font-size: var(--small-font-size);
  font-weight: 600;
  margin: 0;
`;

const StyledInactiveNotification = styled.div`
  display: flex;
  align-items: flex-start;
  border-radius: var(--small-border-radius);
  background: var(--blue-pale);
  max-width: 55%;
  padding: var(--medium-padding) var(--default-padding)
    var(--default-padding-small) var(--medium-padding);
  gap: var(--small-padding);
  font-size: var(--small-font-size);
  font-weight: 500;

  svg {
    font-size: var(--body-font-size);
    color: var(--blue-dark);
    flex-shrink: 0;
    margin-top: var(--xxsmall-padding);
  }
`;

// Content released before series existed carries no value, and the release
// fan-out treats those as Essentials, so the timeline must match

// mirrors the schedule key the handler matches on, so "unchanged" means the
// same thing on both sides
const scheduleSignature = (course: PlannerCRUDCourse): string =>
  [
    course.enrollDate
      ? moment.utc(course.enrollDate).startOf('day').toISOString()
      : '',
    course.enrollmentHour ?? DEFAULT_ENROLLMENT_HOUR,
    !!course.enrollImmediately,
  ].join('|');

// A session counts as automatic while it still carries the settings it was
// offered or loaded with. Comparing against the course's current release date
// instead would reclassify every saved session the moment that date moves,
// even though the stored provenance still says otherwise.
const isAutoScheduled = (course: PlannerCRUDCourse): boolean => {
  if (course.executedAt) {
    return false;
  }

  if (!course.isUpcoming && course.source !== CourseScheduleSource.AUTO) {
    return false;
  }

  if (!course.autoBaseline) {
    return false;
  }

  return scheduleSignature(course) === course.autoBaseline;
};

// A course belongs only to the series it is assigned. Content carrying none
// belongs to no series and is not picked up by any subscription.
const isSeriesSubscribed = (
  subscribedSeries: ContentSeries[],
  contentSeries?: ContentSeries,
): boolean => !!contentSeries && subscribedSeries.includes(contentSeries);

const getNextSubscribedSeries = (
  subscribedSeries: ContentSeries[],
  series: ContentSeries,
  nextValue: boolean,
): ContentSeries[] => {
  const next = new Set(subscribedSeries);

  if (nextValue) {
    next.add(series);
  } else {
    next.delete(series);
  }

  return Object.values(ContentSeries).filter((item) => next.has(item));
};

type CreateUpdatePlannerModalProps = {
  plan?: CoursePlanner;
  refetch?: () => void;
  plannerType: PlannerType | undefined;
  plannerTemplateType?: PlannerTemplateType;
} & DialogProps;

export const CreateUpdatePlannerModal: React.FC<
  CreateUpdatePlannerModalProps
> = ({ plan, visible, onHide, refetch, plannerType, plannerTemplateType }) => {
  const { can } = usePermission();
  const { setDialogData } = useContext(DialogContext);
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const [planName, setPlanName] = useState<string>('');
  const [autoEnroll, setAutoEnroll] = useState(false);
  const [autoEnrollTemp, setAutoEnrollTemp] = useState(false);
  const [subscribedSeries, setSubscribedSeries] = useState<ContentSeries[]>([]);
  const [isSchedulingExpanded, setIsSchedulingExpanded] = useState(true);
  const [pendingSeriesToggle, setPendingSeriesToggle] =
    useState<PendingSeriesToggle | null>(null);
  const [isAutoEnrollConfirmVisibile, setIsAutoEnrollConfirmVisibile] =
    useState(false);
  const [isMakeActiveConfirmVisible, setIsMakeActiveConfirmVisible] =
    useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isActivateConfirmVisible, setIsActivateConfirmVisible] =
    useState(false);
  const [targetSelectorUsersCount, setTargetSelectorUsersCount] = useState(0);

  const [includedEntities, setIncludedEntities] = useState<TargetFormValue[]>(
    [],
  );
  const [excludedEntities, setExcludedEntities] = useState<ExcludeFormValue[]>(
    [],
  );

  const [selectedCourses, setSelectedCourses] = useState<PlannerCRUDCourse[]>(
    [],
  );

  const haveSchedulingDefaultsBeenAppliedRef = useRef(false);

  const isOnboardingPlan = plannerType === PlannerType.ONBOARDING_PLAN;

  const defaultSubscribedSeries: ContentSeries[] =
    plannerType === PlannerType.TRAINING_PLAN ? [ContentSeries.ESSENTIALS] : [];

  const goLiveDatePassed =
    !!account?.awarenessGoLiveDate &&
    new Date(account.awarenessGoLiveDate) <= new Date();

  const setSelectedSortedCourses = (courses: PlannerCRUDCourse[]) => {
    const sortedCourses = getSortedCoursesInPlanner(
      courses,
      plannerType === PlannerType.ONBOARDING_PLAN,
    );
    setSelectedCourses(sortedCourses);
  };

  const today = useMemo(() => new Date(), []);

  const { courses: upcomingCourses, isLoading: areUpcomingCoursesLoading } =
    useCoursesPartialRequest({
      accountId: account?.id as string,
      filters: [
        equal('deletedAt', null),
        notEqual('status', CourseStatus.RETIRED),
        equal('type', MaterialType.COURSE),
        notEqual('plannerScheduleDate', null),
        greater('plannerScheduleDate', today),
      ],
      enabled: visible && plannerType === PlannerType.TRAINING_PLAN,
    });

  const upcomingCoursesToInject = useMemo(
    () =>
      upcomingCourses?.map((course) => ({
        ...course,
        enrollDate: course?.plannerScheduleDate
          ? moment.utc(course.plannerScheduleDate).set({ h: 9, m: 0 }).toDate()
          : undefined,
        enrollmentHour: DEFAULT_ENROLLMENT_HOUR,
        enrollImmediately: false,
        isUpcoming: true,
        autoBaseline: scheduleSignature({
          id: course.id,
          enrollDate: course?.plannerScheduleDate
            ? moment
                .utc(course.plannerScheduleDate)
                .set({ h: 9, m: 0 })
                .toDate()
            : undefined,
          enrollmentHour: DEFAULT_ENROLLMENT_HOUR,
          enrollImmediately: false,
          skipUsersEnrolledWithinGracePeriod: !isOnboardingPlan,
        }),
        skipUsersEnrolledWithinGracePeriod: !isOnboardingPlan,
      })) ?? [],
    [upcomingCourses, isOnboardingPlan],
  );

  const {
    courses: recommendedCourses,
    isLoading: areRecommendedCoursesLoading,
  } = useCoursesPartialRequest({
    accountId: account?.id as string,
    filters: [
      notEqual('status', CourseStatus.RETIRED),
      group(GroupCondition.OR, [
        equal('type', MaterialType.COURSE),
        equal('type', MaterialType.CUSTOM_MATERIAL),
      ]),
      equal('isStandardCatalogue', true),
      ...(plannerType ? [contains('label', [plannerType])] : []),
      ...(plannerType === PlannerType.ONBOARDING_PLAN
        ? [
            group(GroupCondition.AND, [
              group(GroupCondition.OR, [
                lower('plannerScheduleDate', today, true),
                equal('plannerScheduleDate', null),
              ]),
              contains('label', [plannerType]),
            ]),
          ]
        : []),
    ],
    sort: [['priority', 'asc'].join(',')],
    enabled:
      !!plannerType &&
      plannerTemplateType === PlannerTemplateType.RECOMMENDED &&
      visible,
  });

  useEffect(() => {
    haveSchedulingDefaultsBeenAppliedRef.current = false;
    setIncludedEntities([]);
    setExcludedEntities([]);
    setSelectedCourses([]);
    setIsActive(false);
    setPendingSeriesToggle(null);
    setIsAutoEnrollConfirmVisibile(false);
    setAutoEnroll(plannerType === PlannerType.TRAINING_PLAN);
    setSubscribedSeries(defaultSubscribedSeries);
    setIsSchedulingExpanded(true);

    if (plan?.id || !plannerType || !account?.id) {
      return;
    }

    const handleGeneratePlannerName = async () => {
      const response = await client.planner.getPlanners(
        {
          skip: 0,
          take: 0,
          filters: [equal('type', plannerType)],
        },
        account?.id,
      );

      if (response?.count !== undefined) {
        setPlanName(
          plan?.name ?? plannerType === PlannerType.TRAINING_PLAN
            ? `${t('account.trainingPlan')} ${t('generic.for')} ${
                account?.name
              } #${response?.count + 1}`
            : `${t('planner.type.onboardingPlan')} ${t('generic.for')} ${
                account?.name
              } #${response?.count + 1}`,
        );
      }
    };

    handleGeneratePlannerName();
  }, [visible]);

  useEffect(() => {
    if (plan) {
      setPlanName(plan.name);
      setAutoEnroll(!!plan.autoEnroll);
      setSubscribedSeries(
        plan.subscribedSeries ??
          (plan.autoEnroll ? [ContentSeries.ESSENTIALS] : []),
      );
      setIsActive(!!plan.isActive);

      const { includedEntities, excludedEntities } =
        processIncludeExcludeTargetEntities({
          target: plan.targets,
          excludes: plan.excludes,
          account: account as Account,
        });

      setIncludedEntities(includedEntities);
      setExcludedEntities(excludedEntities);

      const planCourses =
        plannerType === PlannerType.ONBOARDING_PLAN
          ? plan.dynamicCourseSchedules
          : plan.staticCourseSchedules;

      setSelectedSortedCourses(
        planCourses.map((item) => {
          const loaded = {
            ...item,
            ...item.course,
            enrollmentHour: item.enrollmentHour ?? DEFAULT_ENROLLMENT_HOUR,
            enrollImmediately: item.enrollImmediately ?? false,
          } as PlannerCRUDCourse;

          return { ...loaded, autoBaseline: scheduleSignature(loaded) };
        }),
      );
    }
  }, [plan]);

  useEffect(() => {
    if (plan) {
      return;
    }
    // Set auto enroll to be enabled for new training plans, once per modal open
    // so a later query settle cannot revert an admin's own toggle choices
    if (plannerType && !haveSchedulingDefaultsBeenAppliedRef.current) {
      haveSchedulingDefaultsBeenAppliedRef.current = true;
      setAutoEnroll(plannerType === PlannerType.TRAINING_PLAN);
      setSubscribedSeries(defaultSubscribedSeries);
    }

    // Set recommended courses or/and upcoming on new plan creation
    if (
      selectedCourses.length ||
      areUpcomingCoursesLoading ||
      areRecommendedCoursesLoading
    )
      return;

    const upcomingToAdd = upcomingCoursesToInject.filter((course) =>
      isSeriesSubscribed(subscribedSeries, course.contentSeries),
    );

    const recommendedToAdd: Course[] =
      recommendedCourses
        ?.filter((course) => !upcomingToAdd?.find((x) => x.id === course.id))
        ?.map((course: Course) => ({
          ...course,
          skipUsersEnrolledWithinGracePeriod: isOnboardingPlan ? false : true,
        })) ?? [];

    setSelectedSortedCourses([
      ...(plannerTemplateType === PlannerTemplateType.RECOMMENDED
        ? recommendedToAdd
        : []),
      ...(plannerType === PlannerType.TRAINING_PLAN && autoEnroll
        ? upcomingToAdd
        : []),
    ]);
  }, [
    areUpcomingCoursesLoading,
    areRecommendedCoursesLoading,
    recommendedCourses,
    upcomingCourses,
    plannerTemplateType,
    plannerType,
    subscribedSeries,
    autoEnroll,
  ]);

  const updateCourseDate = (date: Date, item: PlannerCRUDCourse) => {
    const isToday = moment(date).isSame(moment(), 'day');
    const enrollDate = isToday
      ? moment.utc(new Date())
      : moment.utc(date).set({ h: 9, m: 0 });

    setSelectedCourses((prev) => {
      const courses = prev.map((course: PlannerCRUDCourse) => {
        if (course.id === item.id && course.enrollDate === item.enrollDate) {
          return { ...course, enrollDate: enrollDate.toDate() };
        }
        return course;
      });
      return getSortedCoursesInPlanner(
        courses as PlannerCRUDCourse[],
        plannerType === PlannerType.ONBOARDING_PLAN,
      );
    });
  };

  const updateCourseEnrollmentHour = (
    enrollmentHour: number,
    item: PlannerCRUDCourse,
  ) => {
    setSelectedCourses(
      (prev) =>
        prev.map((course: PlannerCRUDCourse) => {
          if (course.id === item.id && course.enrollDate === item.enrollDate) {
            return { ...course, enrollmentHour };
          }
          return course;
        }) as PlannerCRUDCourse[],
    );
  };

  const updateCourseEnrollImmediately = (
    enrollImmediately: boolean,
    item: PlannerCRUDCourse,
  ) => {
    setSelectedCourses(
      (prev) =>
        prev.map((course: PlannerCRUDCourse) => {
          if (course.id === item.id && course.enrollDate === item.enrollDate) {
            return { ...course, enrollImmediately };
          }
          return course;
        }) as PlannerCRUDCourse[],
    );
  };

  const updateCoursePeriod = (
    periodValue: number,
    periodUnit: CourseSchedulePeriodUnit,
    itemId: string,
  ) => {
    const getSelectedCourses = () => {
      return selectedCourses.map((course: PlannerCRUDCourse) => {
        if (course.id === itemId) {
          return { ...course, periodValue, periodUnit };
        }

        return course;
      });
    };

    setSelectedSortedCourses(getSelectedCourses());
  };

  const updateCourseSkipUsersWithinGracePeriod = (
    skipUsersEnrolledWithinGracePeriod: boolean,
    item: PlannerCRUDCourse,
  ) => {
    const courses = selectedCourses.map((course: PlannerCRUDCourse) => {
      if (
        course.id === item.id &&
        (course.enrollDate === item.enrollDate ||
          (course.periodValue === item.periodValue &&
            course.periodUnit === item.periodUnit))
      ) {
        return { ...course, skipUsersEnrolledWithinGracePeriod };
      }
      return course;
    });

    setSelectedCourses(courses as PlannerCRUDCourse[]);
  };

  const addUpcoming = (series: ContentSeries[]) => {
    setSelectedCourses((prev) => {
      const upcomingToAdd = upcomingCoursesToInject.filter(
        (course) =>
          isSeriesSubscribed(series, course.contentSeries) &&
          !prev.find((x) => x.id === course.id),
      );

      if (!upcomingToAdd.length) {
        return prev;
      }

      return getSortedCoursesInPlanner(
        [...prev, ...upcomingToAdd],
        plannerType === PlannerType.ONBOARDING_PLAN,
      );
    });
  };

  const removeUpcoming = (series: ContentSeries[] = []) => {
    setSelectedCourses((prev) =>
      prev.filter(
        (course) =>
          !isAutoScheduled(course) ||
          isSeriesSubscribed(series, course.contentSeries),
      ),
    );
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const createPlanner = useCreatePlanner();
  const updatePlanner = useUpdatePlanner();

  const handleSavePlanner = async () => {
    if (isDemoAccount || !account?.id) return;

    const sortedCourses = getSortedCoursesInPlanner(
      selectedCourses,
      plannerType === PlannerType.ONBOARDING_PLAN,
    );

    const payload = {
      accountId: account.id,
      id: plan?.id,
      name: planName,
      courses: sortedCourses.map((course: any, index: number) => ({
        id: course.id,
        enrollDate:
          plannerType === PlannerType.TRAINING_PLAN
            ? course.enrollDate || moment().utc(true).toDate()
            : undefined,
        enrollmentHour:
          plannerType === PlannerType.TRAINING_PLAN
            ? course.enrollmentHour ?? DEFAULT_ENROLLMENT_HOUR
            : undefined,
        enrollImmediately:
          plannerType === PlannerType.TRAINING_PLAN
            ? course.enrollImmediately ?? false
            : undefined,
        enrollOffset:
          plannerType === PlannerType.ONBOARDING_PLAN
            ? {
                value: course.periodValue || 0,
                unit: course.periodUnit || CourseSchedulePeriodUnit.DAYS,
              }
            : undefined,
        skipUsersEnrolledWithinGracePeriod:
          course.skipUsersEnrolledWithinGracePeriod,
        source: isAutoScheduled(course)
          ? CourseScheduleSource.AUTO
          : CourseScheduleSource.MANUAL,
        positionInPlanner: index + 1,
      })),
      type: plannerType || PlannerType.TRAINING_PLAN,
      autoEnroll,
      subscribedSeries,
      isActive,
      ...getTargetsAndExcludes(includedEntities, excludedEntities),
    };

    setIsSubmitting(true);

    try {
      const action = !plan?.id ? createPlanner.create : updatePlanner.update;
      await action(payload);

      if (refetch) {
        await refetch();
      }
      plan?.id
        ? toast?.success(t('toast.success'), t('planner.updated'))
        : toast?.success(t('toast.success'), t('planner.created'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      onHide();
      setIsSubmitting(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const deletePlanner = useDeletePlanner();
  const handleDeletePlanner = async () => {
    if (isDemoAccount || !account?.id || !plan?.id) return;

    setIsDeleting(true);

    try {
      await deletePlanner.delete({
        accountId: account.id,
        planId: plan?.id,
      });

      if (refetch) {
        await refetch();
      }

      toast?.success(t('toast.success'), t('planner.deleted'));
      onHide();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsDeleting(false);
    }
  };

  const headerTemplate = () => (
    <StyledHeaderContainer justify="space-between" wrap="wrap">
      <h1 className="flex-1">
        <EditableText
          key={planName}
          value={planName}
          saveValue={(value: string) => setPlanName(value)}
        />
      </h1>
    </StyledHeaderContainer>
  );

  const handleAutoEnrollToggle = (nextValue: boolean) => {
    setAutoEnrollTemp(nextValue);
    setIsAutoEnrollConfirmVisibile(true);
  };

  const commitSeriesToggle = () => {
    if (!pendingSeriesToggle) {
      return;
    }

    const { series, nextValue } = pendingSeriesToggle;

    const nextSubscribedSeries = getNextSubscribedSeries(
      subscribedSeries,
      series,
      nextValue,
    );

    setSubscribedSeries((prev) =>
      getNextSubscribedSeries(prev, series, nextValue),
    );

    if (!nextSubscribedSeries.length) {
      setAutoEnroll(false);
    }

    nextValue
      ? addUpcoming(nextSubscribedSeries)
      : removeUpcoming(nextSubscribedSeries);

    setPendingSeriesToggle(null);
  };

  const isDeletionDisabled = () => {
    if (plannerType === PlannerType.TRAINING_PLAN) {
      return (
        !!plan?.staticCourseSchedules?.length &&
        plan?.staticCourseSchedules?.some((course) => !course.executedAt)
      );
    }

    return !!plan?.dynamicCourseSchedules?.length;
  };

  const shouldShowsMakeActiveConfirm = () => {
    if (plannerType === PlannerType.TRAINING_PLAN) {
      return selectedCourses?.some((course) => !course.executedAt);
    }

    return !!selectedCourses?.length;
  };

  const getNextTrainingDate = () =>
    selectedCourses
      ?.filter((course) => !course.executedAt)
      ?.map((course) => moment(course.enrollDate))
      .filter((date) => date.isSameOrAfter(moment(), 'day'))
      .sort((date1, date2) => date1.diff(date2))[0];

  const shouldShowNextTrainingDate = () =>
    plannerType !== PlannerType.ONBOARDING_PLAN && !!getNextTrainingDate();

  const entityNameBadges = () =>
    includedEntities
      .map(
        ({ name }) =>
          `<span class="badge">${sanitizeText(name.slice(1))}</span>`,
      )
      .join(' ');

  const handleClickSavePlanner = async () => {
    if (
      plannerType === PlannerType.ONBOARDING_PLAN &&
      isActive &&
      shouldShowsMakeActiveConfirm()
    ) {
      setIsMakeActiveConfirmVisible(true);
      return;
    }

    if (
      plannerType === PlannerType.TRAINING_PLAN &&
      shouldShowsMakeActiveConfirm()
    ) {
      try {
        setIsSubmitting(true);

        const response = await client.planner.targetSelectorPreview(
          getTargetsAndExcludes(includedEntities, excludedEntities),
          account?.id,
        );

        setTargetSelectorUsersCount(response?.usersCount);
        setIsMakeActiveConfirmVisible(true);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      handleSavePlanner();
    }
  };

  const hasBlockEmailsMessage = showBlockEmailsMessage(account);

  const dialogFooter = () => {
    return (
      can(Actions.DELETE, Subjects.COURSE_PLANNER) &&
      plan?.id && (
        <StyledModalFooter justify="space-between">
          <FlexContainer justify="flex-start" align="center" gap={12}>
            <StyledDeleteAction
              className={isDeletionDisabled() ? 'disabled' : ''}
              onClick={() => {
                setDialogData({
                  show: true,
                  type: DialogType.CONFIRMATION,
                  header: t('dialog.delete'),
                  message: t('planner.delete.confirm'),
                  onAccept: handleDeletePlanner,
                });
              }}
            >
              {isDeleting ? (
                <i className="pi pi-spin pi-spinner" />
              ) : (
                <TrashIcon />
              )}
            </StyledDeleteAction>
            {isDeletionDisabled() ? (
              <StyledFooterText className="delete-info">
                {t('planner.delete.info')}
              </StyledFooterText>
            ) : null}
            {plan?.updatedAt && (
              <StyledFooterText>
                <strong>{t('planner.lastUpdatedDate')}</strong>{' '}
                {moment(plan?.updatedAt).format(DateFormats.DATEONLY)}
              </StyledFooterText>
            )}
            {plan?.createdAt && (
              <StyledFooterText>
                <strong>{t('planner.createdDate')}</strong>{' '}
                {moment(plan?.createdAt).format(DateFormats.DATEONLY)}
              </StyledFooterText>
            )}
          </FlexContainer>
        </StyledModalFooter>
      )
    );
  };

  return (
    <StyledDialog
      header={headerTemplate}
      blockScroll
      visible={visible}
      onHide={onHide}
      draggable={false}
      closable={!isSubmitting}
      dismissableMask={false}
      closeOnEscape={false}
      data-testid="create-update-planner-modal"
      footer={dialogFooter()}
    >
      <TargetEntitiesSelector
        className="planner"
        title={t('planner.enrollTo')}
        required
        includedEntities={includedEntities}
        setIncludedEntities={setIncludedEntities}
        excludedEntities={excludedEntities}
        setExcludedEntities={setExcludedEntities}
      />
      {plannerType === PlannerType.TRAINING_PLAN && (
        <AutomaticSchedulingToggles
          autoEnroll={autoEnroll}
          subscribedSeries={subscribedSeries}
          onAutoEnrollToggle={handleAutoEnrollToggle}
          onSeriesToggle={(series, nextValue) =>
            setPendingSeriesToggle({ series, nextValue })
          }
          isExpanded={isSchedulingExpanded}
          onToggleExpanded={() => setIsSchedulingExpanded((prev) => !prev)}
          disabled={areUpcomingCoursesLoading}
        />
      )}
      <StyledGrayFlexContainer direction="column" gap={12} width="100%">
        <PlannerCRUDTimeline
          items={selectedCourses}
          targets={includedEntities}
          excludes={excludedEntities}
          isOnboardingPlan={plannerType === PlannerType.ONBOARDING_PLAN}
          onItemAdd={(item: PlannerCRUDCourse) => {
            if (
              selectedCourses.find(
                (course) =>
                  course.id === item.id &&
                  moment(item.enrollDate).isSame(
                    moment(course.enrollDate),
                    'day',
                  ),
              )
            ) {
              item.enrollDate = moment(item.enrollDate).add(1, 'day').toDate();
            }

            if (plannerType === PlannerType.TRAINING_PLAN) {
              item.skipUsersEnrolledWithinGracePeriod = true;
            }

            setSelectedCourses([...selectedCourses, item]);
          }}
          onItemDateChange={updateCourseDate}
          onItemEnrollmentHourChange={updateCourseEnrollmentHour}
          onItemEnrollImmediatelyChange={updateCourseEnrollImmediately}
          onItemSkipUsersWithinGracePeriodToggle={
            updateCourseSkipUsersWithinGracePeriod
          }
          onItemPeriodChange={updateCoursePeriod}
          onItemRemove={(item: PlannerCRUDCourse) =>
            setSelectedCourses(
              selectedCourses.filter(
                (course: PlannerCRUDCourse) =>
                  !(
                    course.id === item.id &&
                    course.enrollDate === item.enrollDate
                  ),
              ),
            )
          }
        />
      </StyledGrayFlexContainer>
      {isOnboardingPlan && (
        <StyledMakePlanActiveRow>
          <StyledMakePlanActiveLeft>
            <StyledMakePlanActiveLabel>
              {t('planner.onboarding.makePlanActive')}
            </StyledMakePlanActiveLabel>
            <AppSwitch
              checked={isActive}
              withText
              onChange={(e) => {
                if (e.value) {
                  setIsActivateConfirmVisible(true);
                } else {
                  setIsActive(false);
                }
              }}
            />
          </StyledMakePlanActiveLeft>
          {!isActive && !goLiveDatePassed && !!account?.awarenessGoLiveDate && (
            <StyledInactiveNotification>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M8.5 13.5H9.5V8H8.5V13.5ZM9 6.575C9.16667 6.575 9.31267 6.51667 9.438 6.4C9.56267 6.28333 9.625 6.13333 9.625 5.95C9.625 5.78333 9.56267 5.64167 9.438 5.525C9.31267 5.40833 9.16667 5.35 9 5.35C8.83333 5.35 8.68733 5.40833 8.562 5.525C8.43733 5.64167 8.375 5.78333 8.375 5.95C8.375 6.13333 8.43733 6.28333 8.562 6.4C8.68733 6.51667 8.83333 6.575 9 6.575ZM9 18C7.75 18 6.57933 17.7627 5.488 17.288C4.396 16.8127 3.446 16.1707 2.638 15.362C1.82933 14.554 1.18733 13.604 0.712 12.512C0.237333 11.4207 0 10.25 0 9C0 7.75 0.237333 6.579 0.712 5.487C1.18733 4.39567 1.82933 3.44567 2.638 2.637C3.446 1.829 4.396 1.18733 5.488 0.712C6.57933 0.237333 7.75 0 9 0C10.25 0 11.421 0.237333 12.513 0.712C13.6043 1.18733 14.5543 1.829 15.363 2.637C16.171 3.44567 16.8127 4.39567 17.288 5.487C17.7627 6.579 18 7.75 18 9C18 10.25 17.7627 11.4207 17.288 12.512C16.8127 13.604 16.171 14.554 15.363 15.362C14.5543 16.1707 13.6043 16.8127 12.513 17.288C11.421 17.7627 10.25 18 9 18ZM9 17C11.2333 17 13.125 16.225 14.675 14.675C16.225 13.125 17 11.2333 17 9C17 6.76667 16.225 4.875 14.675 3.325C13.125 1.775 11.2333 1 9 1C6.76667 1 4.875 1.775 3.325 3.325C1.775 4.875 1 6.76667 1 9C1 11.2333 1.775 13.125 3.325 14.675C4.875 16.225 6.76667 17 9 17Z"
                  fill="#3F9DDF"
                />
              </svg>
              <span>
                {t('planner.onboarding.inactive.notification', {
                  date: formatDate(
                    account.awarenessGoLiveDate,
                    DateFormats.DATEONLY,
                  ),
                })}
              </span>
            </StyledInactiveNotification>
          )}
        </StyledMakePlanActiveRow>
      )}
      <StyledBottomDiv>
        <FlexContainer justify="flex-start" align="center" gap={12}>
          {hasBlockEmailsMessage && <InlineBlockEmails />}
          <FlexContainer justify="flex-end" align="center" gap={12}>
            <AppButton
              label={t('button.close')}
              severity="secondary"
              type="outlined"
              onClick={onHide}
              isDisabled={isSubmitting}
              className="mr-3"
              data-testid="create-update-planner-cancel"
            />
            <AppButton
              label={t('button.save')}
              isDisabled={
                isSubmitting ||
                !includedEntities?.length ||
                (plannerType === PlannerType.TRAINING_PLAN &&
                  !!selectedCourses.find((x) => !x.enrollDate)) ||
                !planName
              }
              state={
                isSubmitting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
              }
              data-testid="save-create-update-planner-cancel"
              onClick={isDemoAccount ? undefined : handleClickSavePlanner}
            />
          </FlexContainer>
        </FlexContainer>
      </StyledBottomDiv>
      <Dialog
        blockScroll
        visible={isMakeActiveConfirmVisible}
        header={t('planner.makeActive.warn')}
        draggable={false}
        data-testid="make-active-confirm-modal"
        onHide={() => setIsMakeActiveConfirmVisible(false)}
        style={{ width: '465px' }}
        className="v2"
        closable={false}
      >
        {plannerType === PlannerType.TRAINING_PLAN && (
          <>
            <StyledDialogContainer
              dangerouslySetInnerHTML={{
                __html: t('planner.makeActive.training.info.1', {
                  numOfUsers: `<span class="badge">${
                    targetSelectorUsersCount === 1
                      ? t('generic.user.count', {
                          count: targetSelectorUsersCount,
                        })
                      : t('generic.users.count', {
                          count: targetSelectorUsersCount,
                        })
                  }</span>`,
                  entityName: entityNameBadges(),
                }),
              }}
            />

            {shouldShowNextTrainingDate() && (
              <StyledDialogContainer
                dangerouslySetInnerHTML={{
                  __html: t('planner.makeActive.training.info.2', {
                    date: `<span class="badge">${getNextTrainingDate()?.format(
                      DateFormats.DATEONLY,
                    )}</span>`,
                  }),
                }}
              />
            )}
          </>
        )}

        {plannerType === PlannerType.ONBOARDING_PLAN && (
          <StyledDialogContainer
            dangerouslySetInnerHTML={{
              __html: t('planner.makeActive.onboarding.info', {
                entityName: entityNameBadges(),
              }),
            }}
          />
        )}

        <FlexContainer justify="flex-start" className="actions">
          <>
            <AppButton
              label={t('planner.makeActiveNow')}
              className="mr-3"
              onClick={
                isDemoAccount
                  ? undefined
                  : () => {
                      setIsMakeActiveConfirmVisible(false);
                      handleSavePlanner();
                    }
              }
            />
            <AppButton
              label={t('button.cancel')}
              type="outlined"
              onClick={() => {
                setIsMakeActiveConfirmVisible(false);
              }}
            />
          </>
        </FlexContainer>
      </Dialog>
      <Dialog
        blockScroll
        visible={isActivateConfirmVisible}
        header={t('planner.onboarding.activate.header')}
        draggable={false}
        data-testid="activate-onboarding-plan-confirm-modal"
        onHide={() => setIsActivateConfirmVisible(false)}
        style={{ width: '465px' }}
        className="v2"
        closable={false}
      >
        <StyledDialogContainer>
          {t('planner.onboarding.activate.body')}
        </StyledDialogContainer>
        <StyledDialogContainer>
          {t('planner.onboarding.activate.question')}
        </StyledDialogContainer>
        <FlexContainer justify="flex-start" className="actions">
          <AppButton
            label={t('planner.onboarding.activate.action')}
            className="mr-3"
            onClick={() => {
              setIsActive(true);
              setIsActivateConfirmVisible(false);
            }}
          />
          <AppButton
            label={t('button.cancel')}
            type="outlined"
            onClick={() => setIsActivateConfirmVisible(false)}
          />
        </FlexContainer>
      </Dialog>
      <Dialog
        blockScroll
        visible={isAutoEnrollConfirmVisibile}
        header={
          autoEnrollTemp
            ? t('planner.toggle.autoEnrollOn.warn.heading', {
                platformName: account?.platformSettings?.name,
              })
            : t('planner.toggle.autoEnrollOff.warn.heading', {
                platformName: account?.platformSettings?.name,
              })
        }
        draggable={false}
        data-testid="auto-enroll-confirm-modal"
        onHide={() => setIsAutoEnrollConfirmVisibile(false)}
        style={{ width: '600px' }}
      >
        {autoEnrollTemp ? (
          <>
            <p>
              {t('planner.toggle.autoEnrollOn.warn.text.1', {
                platformName: account?.platformSettings?.name,
              })}
            </p>
            <p>
              {t('planner.toggle.autoEnrollOn.warn.text.2', {
                platformName: account?.platformSettings?.name,
              })}
            </p>
          </>
        ) : (
          <>
            <p>
              {t('planner.toggle.autoEnrollOff.warn.text.1', {
                platformName: account?.platformSettings?.name,
              })}
            </p>
            <p>
              {t('planner.toggle.autoEnrollOff.warn.text.2', {
                platformName: account?.platformSettings?.name,
              })}
            </p>
          </>
        )}
        <FlexContainer justify="flex-end" className="mt-5">
          <>
            <AppButton
              label={t('button.cancel')}
              type="outlined"
              className="mr-3"
              onClick={() => {
                setIsAutoEnrollConfirmVisibile(false);
              }}
            />
            <AppButton
              label={
                autoEnrollTemp ? t('generic.enable') : t('generic.disable')
              }
              onClick={() => {
                const nextSubscribedSeries = subscribedSeries.length
                  ? subscribedSeries
                  : defaultSubscribedSeries;

                setAutoEnroll(autoEnrollTemp);
                setIsAutoEnrollConfirmVisibile(false);

                if (!autoEnrollTemp) {
                  removeUpcoming();

                  return;
                }

                setSubscribedSeries(nextSubscribedSeries);
                addUpcoming(nextSubscribedSeries);
              }}
            />
          </>
        </FlexContainer>
      </Dialog>
      <SeriesToggleConfirmDialog
        pendingToggle={pendingSeriesToggle}
        onConfirm={commitSeriesToggle}
        onCancel={() => setPendingSeriesToggle(null)}
      />
    </StyledDialog>
  );
};
